import React from 'react';
import {
  Box, Typography, Chip,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Tooltip,
  CircularProgress,
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import SkeletonWrapper from '../../NewProductList/components/SkeletonWrapper/SkeletonWrapper';
import { formatDateLocale } from '../../../lib/utils.es6';

var TH_STYLE = {
  fontSize: 11, fontWeight: 700, color: '#6B7280',
  textTransform: 'uppercase', letterSpacing: '0.05em',
  bgcolor: '#F9FAFB', borderBottom: '1px solid #E5E7EB',
  py: 1.25, px: 2,
};

var TD_STYLE = {
  fontSize: 13, color: '#374151',
  borderBottom: '1px solid #F3F4F6',
  py: 1.5, px: 2,
};

export default function ViewEmailTemplates(props) {
  var templates = props.templates || [];

  if (props.loading) {
    return <SkeletonWrapper type="table" loading={true} />;
  }

  return (
    <TableContainer sx={{
      border: '1px solid #E5E7EB', borderRadius: 2, overflow: 'hidden',
    }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={TH_STYLE}>Template</TableCell>
            <TableCell sx={TH_STYLE}>Subject</TableCell>
            <TableCell sx={TH_STYLE}>Language</TableCell>
            <TableCell sx={TH_STYLE}>Date</TableCell>
            <TableCell sx={TH_STYLE}>Type</TableCell>
            <TableCell sx={Object.assign({}, TH_STYLE, { textAlign: 'right' })}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {templates.length === 0 && (
            <TableRow>
              <TableCell colSpan={6}>
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <EmailOutlinedIcon sx={{ fontSize: 40, color: '#D1D5DB', mb: 1 }} />
                  <Typography sx={{ fontSize: 14, color: '#9CA3AF' }}>
                    No email templates yet
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#D1D5DB', mt: 0.5 }}>
                    Upload one using the button above
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          )}
          {templates.map(function(row) {
            var label = (row.code || '').replace(/_/g, ' ');
            var lang = row.language ? row.language.toUpperCase() : '';
            var isDeleting = props.deleteLoading && props.deletingGuid === row.guid;
            return (
              <TableRow
                key={row.guid || row.code}
                sx={{
                  '&:hover': { bgcolor: '#FAFAFA' },
                  '&:last-child td': { border: 0 },
                }}
              >
                {/* Template name chip */}
                <TableCell sx={TD_STYLE}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={label + (lang ? ' (' + lang + ')' : '')}
                      size="small"
                      sx={{
                        height: 22, fontSize: 11, fontWeight: 600,
                        bgcolor: '#EFF6FF', color: '#1D4ED8',
                        border: '1px solid #BFDBFE', maxWidth: 260,
                      }}
                    />
                    {row.active === false && (
                      <Chip label="Inactive" size="small" sx={{
                        height: 18, fontSize: 10, fontWeight: 600,
                        bgcolor: '#FEF2F2', color: '#B91C1C',
                        border: '1px solid #FECACA',
                      }} />
                    )}
                  </Box>
                </TableCell>

                {/* Subject */}
                <TableCell sx={TD_STYLE}>
                  <Typography sx={{
                    fontSize: 13, color: '#374151',
                    maxWidth: 220, overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {row.subject || <span style={{ color: '#D1D5DB' }}>—</span>}
                  </Typography>
                </TableCell>

                {/* Language */}
                <TableCell sx={TD_STYLE}>
                  {lang ? (
                    <Chip label={lang} size="small" sx={{
                      height: 20, fontSize: 10, fontWeight: 700,
                      bgcolor: '#EEF2FF', color: '#3730A3',
                      border: '1px solid #C7D2FE',
                    }} />
                  ) : <span style={{ color: '#D1D5DB' }}>—</span>}
                </TableCell>

                {/* Date */}
                <TableCell sx={TD_STYLE}>
                  <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
                    {row.created_at ? formatDateLocale(row.created_at) : '—'}
                  </Typography>
                </TableCell>

                {/* Entity type */}
                <TableCell sx={TD_STYLE}>
                  {row.entity_type ? (
                    <Chip label={row.entity_type} size="small" sx={{
                      height: 20, fontSize: 10, fontWeight: 500,
                      bgcolor: '#F5F3FF', color: '#6D28D9',
                      border: '1px solid #DDD6FE',
                    }} />
                  ) : <span style={{ color: '#D1D5DB' }}>—</span>}
                </TableCell>

                {/* Actions */}
                <TableCell sx={Object.assign({}, TD_STYLE, { textAlign: 'right' })}>
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                    {row.url ? (
                      <Tooltip title="Download template">
                        <IconButton size="small" component="a"
                          href={row.url} target="_blank" rel="noopener noreferrer"
                          onClick={function(e) { e.stopPropagation(); }}
                          sx={{ color: '#0D47A1', p: 0.75 }}
                        >
                          <DownloadOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="No body file">
                        <span>
                          <IconButton size="small" disabled sx={{ p: 0.75, opacity: 0.3 }}>
                            <DownloadOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title="Edit">
                      <IconButton size="small"
                        onClick={function(e) { e.stopPropagation(); props.editEmailTemplate(row); }}
                        sx={{ color: '#0D47A1', p: 0.75 }}
                      >
                        <EditOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={isDeleting ? 'Deleting...' : 'Delete'}>
                      <span>
                        <IconButton size="small"
                          disabled={props.deleteLoading}
                          onClick={function(e) {
                            e.stopPropagation();
                            if (!props.deleteLoading) { props.deleteEmailTemplate(row.guid); }
                          }}
                          sx={{ color: '#B91C1C', p: 0.75 }}
                        >
                          {isDeleting
                            ? <CircularProgress size={14} sx={{ color: '#B91C1C' }} />
                            : <DeleteOutlineOutlinedIcon sx={{ fontSize: 16 }} />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
